import { bugsnagNotify } from './bugsnag';

interface Choice {
  id: number;
  text: string;
}

export const INLINE_DRAG_MARKER = 'magoosh-inline-drag';

export interface InlineDragAttempt {
  sequence: number;
  startedAt: number;
  choiceId: number;
  sourceKind: 'bank' | 'blank';
  clearReason: 'mousedown' | 'dragstart' | 'dragend' | 'outside_drop' | 'react_drop' | 'cleanup' | null;
  clearedMs: number | null;
  reactDropHandled: boolean;
  dropIndex: number | null;
  dropChoiceId: number | null;
  updateRequested: boolean;
  stateEffectObserved: boolean;
  handledMs: number | null;
}

export const inlineDragElapsedMs = (startedAt: number): number => (
  Math.min(600000, Math.max(0, Date.now() - startedAt))
);

interface Options {
  bank: HTMLElement;
  targets: HTMLElement[];
  choices: Choice[];
  getLocalChoice: () => Choice | null;
  getAttempt: () => InlineDragAttempt | null;
  promptId: number;
  answerId: number;
}

// A page-wide ceiling also bounds reports when practice navigation mounts new questions.
const reportsSent = { failure: 0, recovered: 0, ignored: 0 };
const reportLimits = { failure: 10, recovered: 2, ignored: 1 };
type ReportKind = keyof typeof reportsSent;

interface NativeDrag {
  startedAt: number;
  enteredTarget: boolean;
  previousSequence: number | null;
}

export function observeInlineDrag(options: Options): () => void {
  const { bank, targets, choices, getLocalChoice, getAttempt, promptId, answerId } = options;
  const reported = new Set<string>();
  const timers = new Set<number>();
  let missedStarts = 0;
  let missedDrops = 0;
  let press: { x: number; y: number; started: boolean } = null;
  let drag: NativeDrag = null;
  let pointerType = 'unknown';

  const attemptFor = (nativeDrag: NativeDrag): InlineDragAttempt | null => {
    const attempt = getAttempt();
    return nativeDrag && attempt && attempt.sequence !== nativeDrag.previousSequence ? attempt : null;
  };

  const attemptDetails = (attempt: InlineDragAttempt) => {
    if (!attempt) return null;
    // Only this fixed schema leaves the page. Never include text or exception messages.
    const { startedAt, ...details } = attempt;
    return { ...details, elapsedMs: inlineDragElapsedMs(startedAt) };
  };

  const targetAt = (node: EventTarget): number => node instanceof Node ?
    targets.findIndex((target) => target.contains(node)) : -1;
  const isOurs = (node: EventTarget): boolean => node instanceof Node &&
    (bank.contains(node) || targetAt(node) !== -1);
  const isDraggableChoice = (node: EventTarget): boolean => isOurs(node) &&
    node instanceof Element && !!node.closest('.draggable-text[draggable="true"]');

  const later = (callback: () => void) => {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      safely(callback);
    }, 0);
    timers.add(timer);
  };

  // Observers must never affect the interaction, including when the reporter is blocked.
  const safely = (callback: () => void) => {
    try { callback(); } catch (_) { /* Diagnostics are best effort. */ }
  };

  const report = (reason: string, details: Record<string, unknown>, kind: ReportKind = 'failure') => {
    const key = kind === 'failure' ? reason : kind;
    if (reported.has(key) || reportsSent[kind] >= reportLimits[kind]) return;
    const error = new Error(`Inline drag diagnostic: ${reason}`);
    error.name = 'InlineDragDiagnostic';
    if (bugsnagNotify(error, (event) => {
      event.severity = 'warning';
      event.groupingHash = `inline-drag-v2:${reason}`;
      event.addMetadata('inlineDrag', {
        version: 2, reason, kind, promptId, answerId, interactionMode: 'desktop-drag',
        targetCount: targets.length, choiceCount: choices.length, pointerType, ...details
      });
    })) {
      reported.add(key);
      reportsSent[kind] += 1;
    }
  };

  const pointerDown = (event: PointerEvent) => {
    pointerType = isOurs(event.target) && ['mouse', 'touch', 'pen'].includes(event.pointerType) ?
      event.pointerType : 'unknown';
  };

  const mouseDown = (event: MouseEvent) => {
    press = null;
    drag = null;
    if (event.button !== 0 || !isDraggableChoice(event.target)) return;
    press = { x: event.clientX, y: event.clientY, started: false };
  };

  const mouseUp = (event: MouseEvent) => {
    const attempt = press;
    press = null;
    if (!attempt || attempt.started || targetAt(event.target) === -1) return;
    if (Math.hypot(event.clientX - attempt.x, event.clientY - attempt.y) < 10) return;
    missedStarts += 1;
    if (missedStarts >= 3) report('repeated_missing_dragstart', {
      attempts: missedStarts, targetIndex: targetAt(event.target), attempt: null
    });
  };

  const dragStart = (event: DragEvent) => {
    drag = null;
    if (!isDraggableChoice(event.target)) return;
    if (press) press.started = true;
    const previous = getAttempt();
    const nativeDrag = {
      startedAt: Date.now(), enteredTarget: false,
      previousSequence: previous ? previous.sequence : null
    };
    drag = nativeDrag;
    // Read after the component and any extension handlers have run.
    later(() => {
      if (event.defaultPrevented) report('dragstart_prevented', {
        defaultPrevented: true, attempt: attemptDetails(attemptFor(nativeDrag))
      });
    });
  };

  const dragOver = (event: DragEvent) => {
    if (drag && targetAt(event.target) !== -1) drag.enteredTarget = true;
  };

  const drop = (event: DragEvent) => {
    const targetIndex = targetAt(event.target);
    if (targetIndex === -1) return;
    const nativeDrag = drag;
    const attempt = attemptFor(nativeDrag);
    drag = null;
    press = null;
    const transfer = event.dataTransfer;
    const localChoice = getLocalChoice();
    const expectedChoice = attempt && choices.find((choice) => choice.id === attempt.choiceId);
    const inputBefore = targets[targetIndex].querySelector('input[type="hidden"]') as HTMLInputElement;
    const inputHadValueBefore = !!inputBefore?.value;
    const inputMatchedBefore = !!expectedChoice && inputBefore?.value === expectedChoice.text;
    let markerStatus = 'missing';
    try {
      const marker = transfer ? transfer.getData('text/plain') : '';
      markerStatus = marker === INLINE_DRAG_MARKER ? 'present' : marker ? 'different' : 'missing';
    } catch (_) {
      markerStatus = 'unreadable';
    }
    const details = {
      targetIndex, markerStatus,
      localChoiceId: localChoice ? localChoice.id : null,
      inputHadValueBefore, inputMatchedBefore, pointerType,
      // Avoid recording arbitrary MIME strings from external drag sources.
      hasPlainText: !!transfer && Array.from(transfer.types).includes('text/plain'),
      typeCount: transfer ? transfer.types.length : 0,
      effectAllowed: transfer?.effectAllowed, dropEffect: transfer?.dropEffect,
      localDragObserved: !!nativeDrag, enteredTarget: !!nativeDrag?.enteredTarget,
      durationMs: nativeDrag ? inlineDragElapsedMs(nativeDrag.startedAt) : null
    };
    later(() => {
      if (!targets[targetIndex].isConnected) return;
      const input = targets[targetIndex].querySelector('input[type="hidden"]') as HTMLInputElement;
      const placementObserved = !!expectedChoice && input?.value === expectedChoice.text;
      const result = {
        ...details, placementObserved, placementChanged: placementObserved && !inputMatchedBefore,
        dropDefaultPrevented: event.defaultPrevented, attempt: attemptDetails(attempt)
      };
      if (placementObserved && attempt?.updateRequested) {
        missedStarts = 0;
        missedDrops = 0;
      }
      if (!nativeDrag) {
        report('external_drop_ignored', result, 'ignored');
      } else if (!attempt) {
        report('start_handler_not_run', result);
      } else if (!attempt.reactDropHandled) {
        report('handler_not_run', result);
      } else if (attempt.dropChoiceId === null) {
        report('local_ref_lost', result);
      } else if (!attempt.updateRequested || !placementObserved) {
        report('update_not_reflected', result);
      } else if (markerStatus !== 'present') {
        if (inputMatchedBefore) {
          report('marker_issue_no_change', result, 'ignored');
        } else {
          report('recovered_marker_issue', result, 'recovered');
        }
      }
    });
  };

  const dragEnd = (event: DragEvent) => {
    if (!isOurs(event.target) || !drag) return;
    const nativeDrag = drag;
    const attempt = attemptFor(nativeDrag);
    drag = null;
    press = null;
    const targetIndex = targetAt(document.elementFromPoint(event.clientX, event.clientY));
    // A cancelled drag away from a blank is normal. Repeated releases over a blank
    // without a drop are a diagnostic signal, not proof of an application bug.
    if (targetIndex === -1) return;
    missedDrops += 1;
    if (missedDrops >= 3) report('repeated_missing_drop', {
      attempts: missedDrops, targetIndex, enteredTarget: nativeDrag.enteredTarget,
      dropEffect: event.dataTransfer?.dropEffect, attempt: attemptDetails(attempt)
    });
  };

  const listeners: { name: string; listener: (event: Event) => void }[] = [
    { name: 'pointerdown', listener: pointerDown },
    { name: 'mousedown', listener: mouseDown }, { name: 'mouseup', listener: mouseUp },
    { name: 'dragstart', listener: dragStart }, { name: 'dragover', listener: dragOver },
    { name: 'drop', listener: drop }, { name: 'dragend', listener: dragEnd }
  ];
  const observers = listeners.map(({ name, listener }) => {
    const observer = (event: Event) => safely(() => listener(event));
    document.addEventListener(name, observer, true);
    return { name, observer };
  });
  return () => {
    observers.forEach(({ name, observer }) => document.removeEventListener(name, observer, true));
    timers.forEach((timer) => window.clearTimeout(timer));
  };
}
